import React from 'react'
import type { TrackCar } from '@nyos/shared'

/**
 * TrackStrip — a physical rail: cars rendered as tiles on a track line,
 * sequenced A-end → B-end (API order). The locomotive glyph pins the pull
 * end; clicking it flips A↔B. Clicking a car anchors a cut — the
 * contiguous block from the pull end through the anchor highlights,
 * making buried-car auto-inclusion visible instead of abstract.
 *
 * Tile width is proportional to car length (feet) so the strip reads like
 * the yard — a 90-ft flat looks different from a 50-ft boxcar.
 */

export type PullEnd = 'A' | 'B'

interface TrackStripProps {
  trackName: string
  cars: TrackCar[]
  pullEnd: PullEnd
  onFlipPullEnd: () => void
  cutAnchorIdx: number | null
  onSelectCar: (idx: number) => void
  loading?: boolean
  fetchedAt?: Date | null
}

const MIN_TILE_W = 52
const MAX_TILE_W = 150
const FT_TO_PX = 1.4

function tileWidth(lengthFeet?: number): number {
  const ft = lengthFeet ?? 50
  return Math.max(MIN_TILE_W, Math.min(MAX_TILE_W, Math.round(ft * FT_TO_PX)))
}

const TrackStrip: React.FC<TrackStripProps> = ({
  trackName,
  cars,
  pullEnd,
  onFlipPullEnd,
  cutAnchorIdx,
  onSelectCar,
  loading,
  fetchedAt,
}) => {
  const inCut = (idx: number) =>
    cutAnchorIdx !== null && (pullEnd === 'A' ? idx <= cutAnchorIdx : idx >= cutAnchorIdx)

  const locoTile = (
    <button
      key="loco-anchor"
      onClick={onFlipPullEnd}
      title={`Engine coupled at ${pullEnd}-end — click to flip pull direction`}
      className="flex-shrink-0 flex flex-col items-center justify-center w-14 h-12 rounded-md border-2 border-dashed border-accent-blue/70 bg-accent-blue/10 hover:bg-accent-blue/20 transition-colors"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent-blue">
        <rect x="4" y="3" width="16" height="14" rx="2" />
        <line x1="4" y1="10" x2="20" y2="10" />
        <circle cx="8" cy="19" r="1.5" />
        <circle cx="16" cy="19" r="1.5" />
      </svg>
      <span className="text-xs font-bold text-accent-blue mt-0.5">LOCO</span>
    </button>
  )

  const carTiles = cars.map((car, idx) => {
    const selected = inCut(idx)
    const isAnchor = idx === cutAnchorIdx
    return (
      <button
        key={car.railCarId}
        onClick={() => onSelectCar(idx)}
        style={{ width: tileWidth(car.lengthFeet) }}
        title={`${car.reportingMark ?? car.railCarId}${car.lengthFeet ? ` · ${car.lengthFeet} ft` : ''}${car.spotIdentifier ? ` · @${car.spotIdentifier}` : ''}`}
        className={`flex-shrink-0 h-12 rounded-md border flex flex-col items-center justify-center px-1 transition-colors ${
          selected
            ? 'bg-primary/25 border-primary'
            : 'bg-railcar-fill/20 border-border hover:bg-muted'
        } ${isAnchor ? 'ring-2 ring-primary ring-offset-1 ring-offset-card' : ''}`}
      >
        <span className="text-xs font-mono font-semibold text-foreground truncate w-full text-center">
          {car.reportingMark ?? car.railCarId.slice(0, 8)}
        </span>
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          {car.lengthFeet != null && <span>{car.lengthFeet}′</span>}
          {car.hazardous && <span className="text-destructive font-bold">◆HAZ</span>}
          {car.loadStatus === 'EMPTY' && <span>E</span>}
        </span>
      </button>
    )
  })

  return (
    <section className="shrink-0 rounded-lg border border-subtle bg-card p-3 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Source — {trackName || 'select a track'}
        </div>
        {fetchedAt && (
          <div className="text-xs font-mono text-muted-foreground">
            yard state {fetchedAt.toLocaleTimeString()}
          </div>
        )}
      </div>

      <div className="flex items-center gap-0 text-xs uppercase tracking-widest text-muted-foreground mb-1 px-14">
        <span>A-end</span>
        <span className="flex-1" />
        <span>B-end</span>
      </div>

      <div className="relative flex-1 overflow-x-auto pb-1">
        {/* rail line */}
        <div className="absolute left-0 right-0 top-[42px] h-[3px] bg-track-line rounded" />
        <div className="relative flex items-end gap-1.5 min-h-[56px]">
          {pullEnd === 'A' && locoTile}
          {loading && (
            <div className="flex-1 h-12 rounded-md border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">
              Loading cars…
            </div>
          )}
          {!loading && cars.length === 0 && trackName && (
            <div className="flex-1 h-12 rounded-md border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">
              Track is empty — seed a scenario or pick another track.
            </div>
          )}
          {!loading && !trackName && (
            <div className="flex-1 h-12 rounded-md border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">
              Pick a track from the index.
            </div>
          )}
          {carTiles}
          {pullEnd === 'B' && locoTile}
        </div>
      </div>

      {cutAnchorIdx !== null && (
        <div className="mt-1.5 text-xs text-muted-foreground">
          Cut: {pullEnd === 'A' ? cutAnchorIdx + 1 : cars.length - cutAnchorIdx} car(s) pulled from{' '}
          <span className="font-semibold text-foreground">{pullEnd}-end</span> — cars between the
          engine and your pick come along automatically.
        </div>
      )}
    </section>
  )
}

export default React.memo(TrackStrip)
